import {transparentize} from 'polished';
import React, {CSSProperties, FC, PropsWithChildren, RefObject, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {
  findAncestorByTag,
  findRelativeOffset,
  isElement,
  isNode,
} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {useLazyUnmountCancelToken} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  EventHandler,
  FrontClickEvent,
  FrontEvent,
  FrontFocusEvent,
  FrontMouseEvent,
} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
  UploadedAttachmentModel,
} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {AttachmentDownloader, AttachmentPreviewer} from '../../../../../services/browser';

/*
 * Constants.
 */

/** Minimum width required for the image controls to appear. */
const minWidthPx = 100;
/** Minimum height required for the image controls to appear. */
const minHeightPx = 40;

/*
 * Props.
 */

interface MessageViewerImageControlsProps {
  attachments: ReadonlyArray<AttachmentModel>;
  onPreviewAttachmentClickAsync: AttachmentPreviewer;
  onDownloadAttachmentClickAsync: AttachmentDownloader;
}

/*
 * Style.
 */

interface WrapperStyleProps {
  $hasOverlay: boolean;
}
const StyledWrapperDiv = styled.div<WrapperStyleProps>`
  position: relative;

  ${(p) => mayAddCursorZoomIn(p.$hasOverlay)}
`;

// Since the overlay has "pointer-events: none", we set the zoom effect on the wrapper.
function mayAddCursorZoomIn(hasOverlay: boolean) {
  if (!hasOverlay) {
    return '';
  }

  return css`
    &:hover {
      cursor: zoom-in;
    }
  `;
}

const StyledOverlayDiv = styled.div`
  position: absolute;
  pointer-events: none;
`;

const StyledDownloadButton = styled(ChromelessButton)`
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 2px;

  pointer-events: all;
  border-radius: 50%;
  background-color: ${(p) => transparentize(0.3, p.theme.greys.shade90)};
  ${(p) => boxShadow(p.theme, `0px 2px 10px ${p.theme.alphas.black40}`)};

  &:hover {
    background-color: ${(p) => p.theme.greys.shade90};
  }
`;

/*
 * Component.
 */

interface Overlay {
  attachment: UploadedAttachmentModel;
  top: number;
  left: number;
  width: number;
  height: number;
}

export const MessageViewerImageControls: FC<PropsWithChildren<MessageViewerImageControlsProps>> = (props) => {
  const theme = useUserTheme();
  const [overlay, setOverlay] = useState<Overlay>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const buildUnmountCancelToken = useLazyUnmountCancelToken();

  const {attachments, onPreviewAttachmentClickAsync, onDownloadAttachmentClickAsync, children} = props;

  /*
   * Event handlers.
   */

  const onWrapperMouseOver: EventHandler = (event) => {
    const newOverlay = findOverlayForTarget(attachments, wrapperRef, event);
    if (!newOverlay) {
      return;
    }

    setOverlay((existingOverlay) => {
      // Don't update the state if it's the same attachment.
      if (existingOverlay && existingOverlay.attachment === newOverlay.attachment) {
        return existingOverlay;
      }

      return newOverlay;
    });
  };

  const onWrapperMouseOut: (event: FrontMouseEvent | FrontFocusEvent) => void = (event) => {
    // If the over node is the button, ignore.
    const currentButton = buttonRef.current;
    if (isNode(event.relatedTarget) && currentButton && currentButton.contains(event.relatedTarget)) {
      return;
    }

    // Otherwise, hide the overlay.
    setOverlay(undefined);
  };

  const onWrapperClick: EventHandler = async (event) => {
    const overlayForTarget = findOverlayForTarget(attachments, wrapperRef, event);
    if (!overlayForTarget) {
      return;
    }

    try {
      await onPreviewAttachmentClickAsync(overlayForTarget.attachment, {
        cancelToken: buildUnmountCancelToken(),
      });
    } catch (error) {
      reportToBugsnag(error);
    }
  };

  /*
   * Render.
   */

  const maybeRenderOverlay = () => {
    if (!overlay) {
      return null;
    }

    const onDownloadButtonClick = async (event: FrontClickEvent) => {
      try {
        await onDownloadAttachmentClickAsync(overlay.attachment, {
          cancelToken: buildUnmountCancelToken(),
          shouldPromptForPath: event.shiftKey,
        });
      } catch (error) {
        reportToBugsnag(error);
      }
    };

    const style: CSSProperties = {
      left: `${overlay.left}px`,
      top: `${overlay.top}px`,
      width: `${overlay.width}px`,
      height: `${overlay.height}px`,
    };

    return (
      <StyledOverlayDiv style={style}>
        <StyledDownloadButton ref={buttonRef} onClick={onDownloadButtonClick}>
          <Icon color={theme.greys.white} name="arrowThickSmall" />
        </StyledDownloadButton>
      </StyledOverlayDiv>
    );
  };

  return (
    <StyledWrapperDiv
      $hasOverlay={Boolean(overlay)}
      ref={wrapperRef}
      onMouseOver={onWrapperMouseOver}
      onMouseOut={onWrapperMouseOut}
      onFocus={onWrapperMouseOver}
      onBlur={onWrapperMouseOut}
      onClick={onWrapperClick}
    >
      {children}
      {maybeRenderOverlay()}
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function findOverlayForTarget(
  attachments: ReadonlyArray<AttachmentModel>,
  wrapperRef: RefObject<HTMLElement | null>,
  event: FrontEvent,
): Overlay | null {
  const currentWrapper = wrapperRef.current;
  const {target} = event;

  // We need a target.
  if (!currentWrapper || !target || !isElement(target)) {
    return null;
  }

  // If our target isn't an image of a minimum size, nothing to do.
  const image = findAncestorByTag(target, currentWrapper, 'IMG');
  if (!image || image.offsetWidth < minWidthPx || image.offsetHeight < minHeightPx) {
    return null;
  }

  // If this image is linked, don't show the controls.
  if (findAncestorByTag(image, currentWrapper, 'A')) {
    return null;
  }

  // Find the corresponding uploaded attachment.
  const attachment = findAttachmentForImage(attachments, image);
  if (!attachment || attachment.status !== AttachmentStatusesEnum.UPLOADED) {
    return null;
  }

  // Show the overlay at the right position with attachment we found.
  const offset = findRelativeOffset(image, currentWrapper);
  return {
    attachment,
    ...offset,
    width: image.offsetWidth,
    height: image.offsetHeight,
  };
}

function findAttachmentForImage(attachments: ReadonlyArray<AttachmentModel>, image: HTMLElement) {
  // Try by CID.
  const cid = image.getAttribute('front-cid');
  if (cid) {
    return findAttachmentByCid(attachments, cid);
  }

  // If none was found, try by URL.
  const url = image.classList.contains('front-inline-img') && image.getAttribute('src');
  if (url) {
    return findAttachmentByUrl(attachments, url);
  }

  return null;
}

function findAttachmentByCid(attachments: ReadonlyArray<AttachmentModel>, cid: string) {
  return attachments.find((a) => a.cid === cid);
}

function findAttachmentByUrl(attachments: ReadonlyArray<AttachmentModel>, url: string) {
  return attachments.find((a) => a.status === AttachmentStatusesEnum.UPLOADED && a.url === url);
}
